import { useEffect, useMemo, useState } from 'react';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, InlineError, TableSkeleton } from '../components/Feedback';
import { Chip, RuleActionChip } from '../components/Chips';
import {
  SelectField,
  TextAreaField,
  TextField,
  ToggleField,
  enumOptions,
  parseEnumValue,
} from '../components/Form';
import { TableWrap } from '../components/Table';
import { Drawer, ConfirmDialog } from '../components/Overlay';
import { RequireRole } from '../lib/auth';
import { documents, syncRules } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import {
  GatewayRole,
  GatewayScenario,
  GatewayScenarioLabels,
  RuleAction,
  RuleActionLabels,
  RuleField,
  RuleFieldKinds,
  RuleFieldLabels,
  RuleOperator,
  RuleOperatorLabels,
  operatorNeedsValue,
  operatorsForField,
} from '../lib/enums';
import { formatDateTime, formatMoney, formatNumber, formatRelative } from '../lib/format';
import type { SyncRuleDto, TestSyncRuleResponse, UpsertSyncRuleRequest } from '../lib/types';

interface ConditionDraft {
  key: string;
  field: RuleField;
  operator: RuleOperator;
  value: string;
}

interface RuleDraft {
  name: string;
  description: string;
  appliesToScenario: GatewayScenario | null;
  action: RuleAction;
  priority: number;
  isEnabled: boolean;
  assignApprovalTo: string;
  conditions: ConditionDraft[];
}

const newConditionKey = () => Math.random().toString(36).slice(2);

const EMPTY_DRAFT: RuleDraft = {
  name: '',
  description: '',
  appliesToScenario: null,
  action: RuleAction.RequireApproval,
  priority: 100,
  isEnabled: true,
  assignApprovalTo: '',
  conditions: [
    { key: newConditionKey(), field: RuleField.InvoiceTotal, operator: RuleOperator.GreaterThan, value: '' },
  ],
};

function toDraft(rule: SyncRuleDto): RuleDraft {
  return {
    name: rule.name,
    description: rule.description ?? '',
    appliesToScenario: rule.appliesToScenario,
    action: rule.action,
    priority: rule.priority,
    isEnabled: rule.isEnabled,
    assignApprovalTo: rule.assignApprovalTo ?? '',
    conditions: [...rule.conditions]
      .sort((a, b) => a.sequence - b.sequence)
      .map((condition) => ({
        key: condition.id,
        field: condition.field,
        operator: condition.operator,
        value: condition.value ?? '',
      })),
  };
}

function toRequest(draft: RuleDraft): UpsertSyncRuleRequest {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    appliesToScenario: draft.appliesToScenario,
    action: draft.action,
    priority: draft.priority,
    isEnabled: draft.isEnabled,
    assignApprovalTo: draft.assignApprovalTo.trim() || null,
    conditions: draft.conditions.map((condition) => ({
      field: condition.field,
      operator: condition.operator,
      value: operatorNeedsValue(condition.operator) ? (condition.value.trim() || null) : null,
    })),
  };
}

function ruleToRequest(rule: SyncRuleDto, overrides: Partial<UpsertSyncRuleRequest>): UpsertSyncRuleRequest {
  return {
    name: rule.name,
    description: rule.description,
    appliesToScenario: rule.appliesToScenario,
    action: rule.action,
    priority: rule.priority,
    isEnabled: rule.isEnabled,
    assignApprovalTo: rule.assignApprovalTo,
    conditions: [...rule.conditions]
      .sort((a, b) => a.sequence - b.sequence)
      .map((condition) => ({
        field: condition.field,
        operator: condition.operator,
        value: condition.value,
      })),
    ...overrides,
  };
}

function SyncRulesContent(): JSX.Element {
  const toast = useToast();
  const list = useAsync((signal) => syncRules.list(signal), []);

  const [editing, setEditing] = useState<SyncRuleDto | 'new' | null>(null);
  const [draft, setDraft] = useState<RuleDraft>(EMPTY_DRAFT);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SyncRuleDto | null>(null);

  const [testDocumentId, setTestDocumentId] = useState('');
  const [testResult, setTestResult] = useState<TestSyncRuleResponse | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  const testCandidates = useAsync((signal) => documents.list({ page: 1, pageSize: 100 }, signal), []);

  useEffect(() => {
    if (editing === 'new') {
      const highest = (list.data ?? []).reduce((max, rule) => Math.max(max, rule.priority), 0);
      setDraft({ ...EMPTY_DRAFT, priority: highest + 10, conditions: [...EMPTY_DRAFT.conditions] });
    } else if (editing) {
      setDraft(toDraft(editing));
    }
    setSaveError(null);
  }, [editing, list.data]);

  const ordered = useMemo(
    () => [...(list.data ?? [])].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name)),
    [list.data],
  );

  const save = async () => {
    if (draft.name.trim() === '') {
      setSaveError('The rule needs a name.');
      return;
    }
    if (draft.conditions.length === 0) {
      setSaveError('Add at least one condition.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const request = toRequest(draft);
      if (editing !== 'new' && editing) {
        await syncRules.update(editing.id, request);
        toast.success('Rule updated', request.name);
      } else {
        await syncRules.create(request);
        toast.success('Rule created', request.name);
      }
      setEditing(null);
      list.reload();
    } catch (error) {
      const message = errorMessage(error);
      setSaveError(message);
      toast.error('Save failed', message);
    } finally {
      setSaving(false);
    }
  };

  const mutate = async (rule: SyncRuleDto, overrides: Partial<UpsertSyncRuleRequest>, successMessage: string) => {
    setBusyRuleId(rule.id);
    try {
      await syncRules.update(rule.id, ruleToRequest(rule, overrides));
      toast.success(successMessage, rule.name);
      list.reload();
    } catch (error) {
      toast.error('Update failed', errorMessage(error));
    } finally {
      setBusyRuleId(null);
    }
  };

  /** Swaps priorities with the neighbouring rule so ordering stays explicit and stable. */
  const move = async (rule: SyncRuleDto, direction: -1 | 1) => {
    const index = ordered.findIndex((item) => item.id === rule.id);
    const neighbour = ordered[index + direction];
    if (!neighbour) return;
    setBusyRuleId(rule.id);
    try {
      await syncRules.update(rule.id, ruleToRequest(rule, { priority: neighbour.priority }));
      await syncRules.update(neighbour.id, ruleToRequest(neighbour, { priority: rule.priority }));
      toast.success('Priority changed', rule.name);
      list.reload();
    } catch (error) {
      toast.error('Reorder failed', errorMessage(error));
    } finally {
      setBusyRuleId(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusyRuleId(deleting.id);
    try {
      await syncRules.remove(deleting.id);
      toast.success('Rule deleted', deleting.name);
      setDeleting(null);
      list.reload();
    } catch (error) {
      toast.error('Delete failed', errorMessage(error));
    } finally {
      setBusyRuleId(null);
    }
  };

  const runTest = async () => {
    if (!testDocumentId) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await syncRules.test({ documentId: testDocumentId });
      setTestResult(result);
    } catch (error) {
      const message = errorMessage(error);
      setTestError(message);
      toast.error('Rule test failed', message);
    } finally {
      setTesting(false);
    }
  };

  const updateCondition = (key: string, patch: Partial<ConditionDraft>) => {
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.map((condition) =>
        condition.key === key ? { ...condition, ...patch } : condition,
      ),
    }));
  };

  return (
    <>
      <PageHeader
        title="Sync Rules"
        description="Rules run in priority order; the first match decides what happens to a document."
        actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={list.reload} disabled={list.loading}>
              Refresh
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              New rule
            </button>
          </>
        }
      />

      <Panel title="Rules">
        {list.loading && !list.data ? (
          <TableSkeleton rows={6} columns={6} />
        ) : list.error ? (
          <ErrorState message="Rules could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : ordered.length === 0 ? (
          <EmptyState
            title="No sync rules"
            description="Without rules every document waits for a human. Add one to automate the easy cases."
            action={
              <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
                New rule
              </button>
            }
          />
        ) : (
          <TableWrap busy={list.loading}>
            <thead>
              <tr>
                <th scope="col" className="col-tiny">
                  Order
                </th>
                <th scope="col">Rule</th>
                <th scope="col">Conditions</th>
                <th scope="col">Action</th>
                <th scope="col">Matches</th>
                <th scope="col">Enabled</th>
                <th scope="col" className="col-actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((rule, index) => (
                <tr key={rule.id} className={rule.isEnabled ? '' : 'row-muted'}>
                  <td>
                    <div className="priority-cell">
                      <span className="priority-value mono">{rule.priority}</span>
                      <span className="priority-buttons">
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          aria-label={`Move ${rule.name} up`}
                          disabled={index === 0 || busyRuleId !== null}
                          onClick={() => void move(rule, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          aria-label={`Move ${rule.name} down`}
                          disabled={index === ordered.length - 1 || busyRuleId !== null}
                          onClick={() => void move(rule, 1)}
                        >
                          ↓
                        </button>
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="cell-strong">{rule.name}</span>
                    {rule.description ? <span className="cell-sub">{rule.description}</span> : null}
                    <span className="cell-sub">
                      {rule.appliesToScenario === null
                        ? 'Any scenario'
                        : GatewayScenarioLabels[rule.appliesToScenario]}
                    </span>
                  </td>
                  <td>
                    <ul className="condition-summary">
                      {[...rule.conditions]
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((condition) => (
                          <li key={condition.id}>
                            <span className="mono">{RuleFieldLabels[condition.field]}</span>{' '}
                            {RuleOperatorLabels[condition.operator]}{' '}
                            {operatorNeedsValue(condition.operator) ? (
                              <strong>{condition.value ?? '—'}</strong>
                            ) : null}
                          </li>
                        ))}
                    </ul>
                  </td>
                  <td>
                    <RuleActionChip action={rule.action} />
                    {rule.assignApprovalTo ? (
                      <span className="cell-sub">to {rule.assignApprovalTo}</span>
                    ) : null}
                  </td>
                  <td>
                    {formatNumber(rule.timesMatched)}
                    <span className="cell-sub">
                      {rule.lastMatchedUtc ? formatRelative(rule.lastMatchedUtc) : 'never'}
                    </span>
                  </td>
                  <td>
                    <ToggleField
                      label={rule.isEnabled ? 'Enabled' : 'Disabled'}
                      checked={rule.isEnabled}
                      disabled={busyRuleId !== null}
                      onChange={(checked) =>
                        void mutate(rule, { isEnabled: checked }, checked ? 'Rule enabled' : 'Rule disabled')
                      }
                    />
                  </td>
                  <td className="col-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(rule)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeleting(rule)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      <Panel
        title="Test rules"
        description="Dry-runs the whole rule set against one document. Nothing is changed."
      >
        <Toolbar>
          <SelectField
            label="Document"
            value={testDocumentId}
            onChange={setTestDocumentId}
            options={(testCandidates.data?.items ?? []).map((doc) => ({
              value: doc.id,
              label: `${doc.vendorName ?? 'Unknown vendor'} · ${doc.invoiceNumber ?? doc.fileName} · ${formatMoney(doc.total, doc.currencyCode)}`,
            }))}
            placeholder={testCandidates.loading ? 'Loading documents…' : 'Select a document'}
            className="toolbar-grow"
          />
          <div className="toolbar-action">
            <button
              type="button"
              className="btn btn-primary"
              onClick={runTest}
              disabled={!testDocumentId || testing}
            >
              {testing ? 'Testing…' : 'Run test'}
            </button>
          </div>
        </Toolbar>

        <InlineError message={testError} />

        {testResult ? (
          <div className="rule-test-result">
            <div className={`banner ${testResult.matchedRuleId ? 'banner-good' : 'banner-neutral'} banner-inline`}>
              <div className="banner-body">
                <p className="banner-title">
                  {testResult.matchedRuleName
                    ? `Matched "${testResult.matchedRuleName}"`
                    : 'No rule matched — the default path applies'}
                </p>
                <p className="banner-detail">{testResult.explanation}</p>
              </div>
              <RuleActionChip action={testResult.resolvedAction} />
            </div>

            <h3 className="sub-title">Evaluation trace</h3>
            <ol className="trace-list">
              {testResult.trace.map((entry, index) => (
                <li key={`${entry.ruleId}-${index}`} className={`trace-item ${entry.matched ? 'trace-matched' : ''}`}>
                  <div className="trace-head">
                    <Chip tone={entry.matched ? 'good' : 'neutral'}>{entry.matched ? 'Matched' : 'Skipped'}</Chip>
                    <span className="trace-name">{entry.ruleName}</span>
                  </div>
                  <ul className="trace-conditions">
                    {entry.conditionResults.map((result, resultIndex) => (
                      <li key={resultIndex} className="mono">
                        {result}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </Panel>

      <Drawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New sync rule' : 'Edit sync rule'}
        description="All conditions must hold for the action to apply."
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save rule'}
            </button>
          </>
        }
      >
        <InlineError message={saveError} />

        <div className="form-grid">
          <TextField
            label="Name"
            value={draft.name}
            onChange={(value) => setDraft((current) => ({ ...current, name: value }))}
            required
            className="form-span-2"
          />
          <SelectField
            label="Applies to scenario"
            value={draft.appliesToScenario === null ? '' : String(draft.appliesToScenario)}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                appliesToScenario: parseEnumValue<GatewayScenario>(value),
              }))
            }
            options={enumOptions(GatewayScenarioLabels)}
            placeholder="Any scenario"
          />
          <SelectField
            label="Action"
            value={String(draft.action)}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                action: parseEnumValue<RuleAction>(value) ?? RuleAction.HoldForReview,
              }))
            }
            options={enumOptions(RuleActionLabels)}
          />
          <TextField
            label="Priority"
            type="number"
            value={String(draft.priority)}
            onChange={(value) =>
              setDraft((current) => ({ ...current, priority: Number(value) || 0 }))
            }
            hint="Lower numbers are evaluated first."
          />
          <TextField
            label="Assign approvals to (email)"
            type="email"
            value={draft.assignApprovalTo}
            onChange={(value) => setDraft((current) => ({ ...current, assignApprovalTo: value }))}
            placeholder="Optional"
            disabled={draft.action !== RuleAction.RequireApproval}
          />
        </div>

        <TextAreaField
          label="Description"
          value={draft.description}
          onChange={(value) => setDraft((current) => ({ ...current, description: value }))}
          placeholder="Why this rule exists."
        />

        <ToggleField
          label="Rule is enabled"
          checked={draft.isEnabled}
          onChange={(checked) => setDraft((current) => ({ ...current, isEnabled: checked }))}
        />

        <div className="sub-section">
          <div className="sub-head">
            <h3 className="sub-title">Conditions</h3>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  conditions: [
                    ...current.conditions,
                    {
                      key: newConditionKey(),
                      field: RuleField.InvoiceTotal,
                      operator: RuleOperator.GreaterThan,
                      value: '',
                    },
                  ],
                }))
              }
            >
              Add condition
            </button>
          </div>

          {draft.conditions.length === 0 ? (
            <EmptyState title="No conditions" description="A rule needs at least one condition." />
          ) : (
            <ul className="condition-rows">
              {draft.conditions.map((condition, index) => {
                const kind = RuleFieldKinds[condition.field];
                const needsValue = operatorNeedsValue(condition.operator);
                return (
                  <li className="condition-row" key={condition.key}>
                    <span className="condition-join">{index === 0 ? 'When' : 'and'}</span>

                    <SelectField
                      label="Field"
                      value={String(condition.field)}
                      onChange={(value) => {
                        const field = parseEnumValue<RuleField>(value) ?? RuleField.InvoiceTotal;
                        const allowed = operatorsForField(field);
                        updateCondition(condition.key, {
                          field,
                          operator: allowed.includes(condition.operator) ? condition.operator : allowed[0],
                          value: '',
                        });
                      }}
                      options={enumOptions(RuleFieldLabels)}
                    />

                    <SelectField
                      label="Operator"
                      value={String(condition.operator)}
                      onChange={(value) =>
                        updateCondition(condition.key, {
                          operator: parseEnumValue<RuleOperator>(value) ?? RuleOperator.Equals,
                        })
                      }
                      options={operatorsForField(condition.field).map((operator) => ({
                        value: String(operator),
                        label: RuleOperatorLabels[operator],
                      }))}
                    />

                    {needsValue && kind === 'scenario' ? (
                      <SelectField
                        label="Value"
                        value={condition.value}
                        onChange={(value) => updateCondition(condition.key, { value })}
                        options={enumOptions(GatewayScenarioLabels)}
                        placeholder="Pick a scenario"
                      />
                    ) : needsValue ? (
                      <TextField
                        label="Value"
                        type={kind === 'number' ? 'number' : 'text'}
                        inputMode={kind === 'number' ? 'decimal' : 'text'}
                        value={condition.value}
                        onChange={(value) => updateCondition(condition.key, { value })}
                        placeholder={kind === 'number' ? '0' : 'Text to compare'}
                      />
                    ) : (
                      <div className="condition-novalue">No value needed</div>
                    )}

                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      aria-label={`Remove condition ${index + 1}`}
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          conditions: current.conditions.filter((item) => item.key !== condition.key),
                        }))
                      }
                    >
                      &times;
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {editing !== 'new' && editing ? (
          <p className="drawer-note">
            Matched {formatNumber(editing.timesMatched)} times ·{' '}
            {editing.lastMatchedUtc ? `last ${formatDateTime(editing.lastMatchedUtc)}` : 'never matched'}
          </p>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete sync rule"
        message={`Delete "${deleting?.name ?? ''}"? Documents will no longer be routed by this rule.`}
        confirmLabel="Delete rule"
        tone="danger"
        pending={busyRuleId !== null}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void remove()}
      />
    </>
  );
}

/** Admin-only screen; the guard is repeated here so it holds however the page is rendered. */
export function SyncRulesPage(): JSX.Element {
  return (
    <RequireRole minRole={GatewayRole.Admin}>
      <SyncRulesContent />
    </RequireRole>
  );
}
